import React, { useEffect, useState } from "react";

/**
 * CoordinatorAccess — which coordinators can see this client (Oct 19).
 *
 * Two modes, set per client by the agency's admin:
 *   all       every coordinator in the agency (the default, the original rule)
 *   selected  only the coordinators ticked here
 *
 * The admin always sees every client, so the admin is never in the list.
 * Everyone else who can manage the client sees the current answer read-only,
 * with no controls: a coordinator must not be able to remove a colleague.
 *
 * Renders nothing for a family account (the endpoint 404s: no coordinators).
 */
export default function CoordinatorAccess({ clientId, clientName, onChanged }) {
  const [data, setData]       = useState(null);
  const [mode, setMode]       = useState("all");
  const [chosen, setChosen]   = useState(new Set());
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [saved, setSaved]     = useState(false);
  const [hidden, setHidden]   = useState(false);

  function adopt(d) {
    setData(d);
    setMode(d.mode);
    setChosen(new Set(d.coordinators.filter(c => c.selected).map(c => c.user_id)));
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/clients/${clientId}/coordinators`);
        if (!r.ok) { if (!cancelled) setHidden(true); return; }
        const d = await r.json();
        if (cancelled) return;
        if (!d || !Array.isArray(d.coordinators)) { setHidden(true); return; }
        adopt(d);
      } catch { if (!cancelled) setHidden(true); }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  if (hidden || !data) return null;

  const original = new Set(data.coordinators.filter(c => c.selected).map(c => c.user_id));
  const dirty = mode !== data.mode
    || (mode === "selected" && (chosen.size !== original.size || [...chosen].some(id => !original.has(id))));

  function toggle(id) {
    setSaved(false);
    setChosen(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function save() {
    setBusy(true); setError(null); setSaved(false);
    try {
      const r = await fetch(`/api/clients/${clientId}/coordinators`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode, user_ids: [...chosen] }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Couldn't save that.");
      adopt(d);
      setSaved(true);
      onChanged?.();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const names = data.coordinators.filter(c => c.selected).map(c => c.display_name);

  // ── Read-only: anyone but the admin ───────────────────────────────────────
  if (!data.can_change) {
    return (
      <div style={s.wrap}>
        <div style={s.head}>Coordinators</div>
        <p style={s.text}>
          {data.mode === "all"
            ? "Every coordinator in your agency can see this profile."
            : names.length
              ? `Only these coordinators can see this profile: ${names.join(", ")}.`
              : "No coordinators are chosen for this profile."}
        </p>
        <p style={s.hint}>Your agency's admin chooses this.</p>
      </div>
    );
  }

  // ── The admin's control ───────────────────────────────────────────────────
  return (
    <div style={s.wrap}>
      <div style={s.head}>Coordinators</div>
      <div role="radiogroup" aria-label="Which coordinators can see this profile" style={s.options}>
        {[
          ["all", "All coordinators", "Everyone who works as a coordinator in your agency, including people who join later."],
          ["selected", "Only these coordinators", `Only the people you tick. Others won't see ${clientName || "this profile"} at all.`],
        ].map(([v, title, sub]) => (
          <label key={v} style={{ ...s.option, ...(mode === v ? s.optionOn : {}) }}>
            <input type="radio" name={`coord-mode-${clientId}`} value={v} checked={mode === v}
                   disabled={busy} onChange={() => { setMode(v); setSaved(false); }} />
            <span>
              <span style={s.optionTitle}>{title}</span>
              <span style={s.optionSub}>{sub}</span>
            </span>
          </label>
        ))}
      </div>

      {mode === "selected" && (
        <div style={s.list}>
          {data.coordinators.length === 0 && (
            <p style={s.hint}>Your agency has no coordinators yet. You can still see this profile.</p>
          )}
          {data.coordinators.map(c => (
            <label key={c.user_id} style={s.check}>
              <input type="checkbox" checked={chosen.has(c.user_id)} disabled={busy}
                     onChange={() => toggle(c.user_id)} />
              <span style={s.checkName}>{c.display_name}</span>
              <span style={s.checkSub}>{c.email}</span>
            </label>
          ))}
          <p style={s.hint}>You can always see every profile, as the admin.</p>
        </div>
      )}

      {error && <div style={s.error}>{error}</div>}
      <div style={s.actions}>
        <button type="button" style={{ ...s.save, ...(!dirty || busy ? s.disabled : {}) }}
                disabled={!dirty || busy} onClick={save}>
          {busy ? "Saving…" : "Save"}
        </button>
        {saved && !dirty && <span style={s.saved}>Saved</span>}
      </div>
    </div>
  );
}

const s = {
  wrap:    { display: "flex", flexDirection: "column", gap: "var(--space-2)", padding: "var(--space-3) 0 var(--space-4)", borderBottom: "1px solid var(--color-border)" },
  head:    { fontSize: 10, fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-tertiary)" },
  text:    { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0 },
  hint:    { fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: 1.5, margin: 0 },
  options: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: "var(--space-2)" },
  option:  { display: "flex", gap: 8, alignItems: "flex-start", padding: "var(--space-3)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", cursor: "pointer", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  optionOn:{ border: "1px solid var(--color-accent)", background: "var(--color-accent-subtle)", color: "var(--color-text-primary)" },
  optionTitle: { display: "block", fontWeight: "var(--weight-semibold)" },
  optionSub:   { display: "block", fontSize: 11, lineHeight: 1.4, color: "var(--color-text-tertiary)", marginTop: 2 },
  list:    { display: "flex", flexDirection: "column", gap: 6, paddingTop: 4 },
  check:   { display: "flex", alignItems: "baseline", gap: 8, fontSize: "var(--text-sm)", cursor: "pointer", flexWrap: "wrap" },
  checkName: { color: "var(--color-text-primary)" },
  checkSub:  { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  actions: { display: "flex", alignItems: "center", gap: "var(--space-3)" },
  save:    { font: "inherit", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  disabled:{ opacity: 0.5, cursor: "not-allowed" },
  saved:   { fontSize: "var(--text-xs)", color: "var(--color-accent)" },
  error:   { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)" },
};
